import { supplierListingsQuery } from "@/api/reporting";
import { api } from "@/api/utils";
import { ChartBox, formatAxisNumber, useContainerSize } from "@/components/charts/ChartBox";
import { TokenCurrencyCode, tokensToMoney } from "@/modules/money-fns";
import { formatDollars, formatWholeNumber } from "@common/utils/i18n";
import { Box, BoxProps, Grid, Text, UnstyledButton } from "@givenwell/components";
import {
  CalendarIcon,
  IconProps,
  ReloadIcon,
  ShoppingBagIcon,
  TaskListIcon,
  TicketIcon,
} from "@givenwell/core-solid-icons";
import { MemberActivityEntry } from "@givenwell/management-api";
import { colors } from "@givenwell/theme";
import { barY, defineChart, rect, ruleY, stack } from "@tanstack/charts";
import { Chart } from "@tanstack/charts/react";
import { scaleBand } from "@tanstack/charts/scales/band";
import { scaleLinear } from "@tanstack/charts/scales/linear";
import { tooltip } from "@tanstack/charts/tooltip";
import { binX } from "@tanstack/charts/transform/bin";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { ComponentPropsWithRef, FunctionComponent, ReactNode, Suspense, useMemo, useState } from "react";

export const Route = createFileRoute("/dashboard/usage")({
  component: Page,
});

function Page() {
  return (
    <Grid
      css={{
        width: "100%",
        height: "100%",

        gap: 12,
        gridCols: 3,
        gridTemplateRows: "repeat(2, 1fr)",
      }}
    >
      <Suspense fallback={<Card />}>
        <MarketplacePurchasesByType />
      </Suspense>
      <Suspense fallback={<Card />}>
        <VariantsByType />
      </Suspense>
      <Suspense fallback={<Card />}>
        <AllowancePlot />
      </Suspense>
      <Suspense fallback={<Card />}>
        <PurchaseGaps />
      </Suspense>
      <Suspense fallback={<Card />}>
        <DormantAccounts />
      </Suspense>
      <Card css={{ flex: "1 0 0px" }} />
    </Grid>
  );
}

const bookingPattern = "booking-pattern";
const productPattern = "product-pattern";
const eventPattern = "event-pattern";
const subscriptionPattern = "subscription-pattern";
const passPattern = "pass-pattern";

type SvgPattern = {
  id: string;
  fill: string;
  Icon: FunctionComponent<IconProps>;
};
const patterns: SvgPattern[] = [
  {
    id: bookingPattern,
    fill: colors.red500,
    Icon: CalendarIcon,
  },
  {
    id: productPattern,
    fill: colors.blue600,
    Icon: ShoppingBagIcon,
  },
  {
    id: eventPattern,
    fill: colors.yellow,
    Icon: TicketIcon,
  },
  {
    id: subscriptionPattern,
    fill: "#f7a139",
    Icon: ReloadIcon,
  },
  {
    id: passPattern,
    fill: colors.green400,
    Icon: TaskListIcon,
  },
];
function Patterns(props: ComponentPropsWithRef<"svg">) {
  return (
    <svg {...props} style={{ width: 0, height: 0 }}>
      <defs>
        {patterns.map(({ id, fill, Icon }) => (
          <pattern
            key={id}
            width="24"
            height="48"
            id={id}
            patternUnits="userSpaceOnUse"
            patternTransform="rotate(-30 50 50)"
          >
            <rect width="24" height="48" fill={fill} />
            <Icon color={"rgba(0,0,0,0.2)"} />
            <Icon x={12} y="24" color={"rgba(0,0,0,0.2)"} />
            <Icon x={-12} y="24" color={"rgba(0,0,0,0.2)"} />
          </pattern>
        ))}
      </defs>
    </svg>
  );
}

const patternFillByVariantType: Record<string, string> = {
  Booking: `url(#${bookingPattern})`,
  Product: `url(#${productPattern})`,
  Event: `url(#${eventPattern})`,
  Subscription: `url(#${subscriptionPattern})`,
  Pass: `url(#${passPattern})`,
};

const monthKeyOf = (date: Date) => `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, "0")}`;
const monthKeyToDate = (monthKey: string) =>
  new Date(Number(monthKey.slice(0, 4)), Number(monthKey.slice(5, 7)) - 1, 1);

const monthTickFmt = new Intl.DateTimeFormat("en-NZ", { month: "short", year: "2-digit" });
const monthTitleFmt = new Intl.DateTimeFormat("en-NZ", { month: "long", year: "numeric" });

type MonthlyVariantCount = {
  monthKey: string;
  month: Date;
  variantType: string;
  count: number;
};

function MarketplacePurchasesByType() {
  const { data: revenue } = useSuspenseQuery({
    queryKey: ["reporting", "purchases"],
    queryFn: async () => {
      return await api.reporting.getManagementReportingObsPurchases();
    },
  });
  const [normalized, setNormalized] = useState(false);
  const { ref, height } = useContainerSize();

  const { rows, monthKeys } = useMemo(() => {
    const countsByKey = new Map<string, MonthlyVariantCount>();
    let firstMonth: Date | undefined;
    let lastMonth: Date | undefined;
    for (const purchase of revenue) {
      const created = new Date(purchase.created_at);
      const month = new Date(created.getFullYear(), created.getMonth(), 1);
      const monthKey = monthKeyOf(month);
      const variantType = purchase.variant_type || "";
      const countKey = `${monthKey}:${variantType}`;
      const existing = countsByKey.get(countKey);
      if (existing) {
        existing.count += 1;
      } else {
        countsByKey.set(countKey, { monthKey, month, variantType, count: 1 });
      }
      if (!firstMonth || month < firstMonth) firstMonth = month;
      if (!lastMonth || month > lastMonth) lastMonth = month;
    }
    // Every month between the first and last purchase, so empty months keep
    // their slot and the band axis stays time-ordered and contiguous.
    const monthKeys: string[] = [];
    if (firstMonth && lastMonth) {
      const cursor = new Date(firstMonth);
      while (cursor <= lastMonth) {
        monthKeys.push(monthKeyOf(cursor));
        cursor.setMonth(cursor.getMonth() + 1);
      }
    }
    return { rows: [...countsByKey.values()], monthKeys };
  }, [revenue]);

  const definition = useMemo(() => {
    return defineChart({
      marks: [
        barY(rows, {
          x: "monthKey",
          y: "count",
          z: "variantType",
          key: row => `${row.monthKey}:${row.variantType}`,
          fill: row => patternFillByVariantType[row.variantType] || colors.blue300,
          inset: 0.5,
          layout: normalized ? stack({ order: "descending", offset: "normalize" }) : stack({ order: "descending" }),
        }),
      ],
      scales: {
        x: {
          scale: scaleBand<string>().domain(monthKeys).padding(0),
          axis: {
            ticks: { size: 0, format: monthKey => monthTickFmt.format(monthKeyToDate(monthKey)) },
          },
        },
        y: { scale: scaleLinear, grid: false, axis: { ticks: { format: formatAxisNumber } } },
      },
      margin: { left: 50 },
      tooltip: {
        use: tooltip,
        format: point => `${point.datum.variantType} ${monthTitleFmt.format(point.datum.month)}`,
      },
    });
  }, [rows, monthKeys, normalized]);

  return (
    <Card
      title="Purchases by Type"
      css={{ display: "flex", flexDirection: "column", flex: "1 0 0px", position: "relative" }}
    >
      <Box css={{ position: "absolute", top: 4, right: 4 }}>
        <UnstyledButton
          onClick={() => setNormalized(!normalized)}
          css={{
            padding: 8,
            borderRadius: 8,
            border: "none",
            color: colors.blue500,
            fontSize: 14,
            cursor: "pointer",
          }}
        >
          {normalized ? "Denormalize" : "Normalize"}
        </UnstyledButton>
      </Box>
      <Patterns />
      <ChartBox ref={ref} css={{ height: "100%", flex: "1 0 0px" }}>
        {height !== undefined && <Chart definition={definition} height={height} ariaLabel="Purchases by type" />}
      </ChartBox>
    </Card>
  );
}

const sum = (arr: number[]) => arr.reduce((a, b) => a + b, 0);

function VariantsByType() {
  const { data: listings } = useSuspenseQuery(supplierListingsQuery());
  const { data: purchases } = useSuspenseQuery({
    queryKey: ["reporting", "purchases"],
    queryFn: async () => {
      return await api.reporting.getManagementReportingObsPurchases();
    },
  });

  // LISTINGS
  const publishedListings = listings.filter(listing => listing.status === "Published");
  const latestPublishedListings = publishedListings.filter(listing => {
    return !publishedListings.some(
      other => other.supplier_id === listing.supplier_id && other.version > listing.version,
    );
  });
  const listingsWithBookingVariants = latestPublishedListings.filter(listing => listing.num_bookings).length;
  const listingsWithProductVariants = latestPublishedListings.filter(listing => listing.num_products).length;
  const listingsWithEventVariants = latestPublishedListings.filter(listing => listing.num_events).length;
  const listingsWithSubscriptionVariants = latestPublishedListings.filter(listing => listing.num_subscriptions).length;
  const listingsWithPassVariants = latestPublishedListings.filter(listing => listing.num_passes).length;

  const listingData = [
    {
      type: "Booking",
      count: listingsWithBookingVariants,
    },
    {
      type: "Product",
      count: listingsWithProductVariants,
    },
    {
      type: "Event",
      count: listingsWithEventVariants,
    },
    {
      type: "Subscription",
      count: listingsWithSubscriptionVariants,
    },
    {
      type: "Pass",
      count: listingsWithPassVariants,
    },
  ];
  listingData.sort((a, b) => b.count - a.count);

  const bookingVariants = sum(latestPublishedListings.map(listing => listing.num_bookings));
  const productVariants = sum(latestPublishedListings.map(listing => listing.num_products));
  const eventVariants = sum(latestPublishedListings.map(listing => listing.num_events));
  const subscriptionVariants = sum(latestPublishedListings.map(listing => listing.num_subscriptions));
  const passVariants = sum(latestPublishedListings.map(listing => listing.num_passes));
  const variantData = [
    {
      type: "Booking",
      count: bookingVariants,
    },
    {
      type: "Product",
      count: productVariants,
    },
    {
      type: "Event",
      count: eventVariants,
    },
    {
      type: "Subscription",
      count: subscriptionVariants,
    },
    {
      type: "Pass",
      count: passVariants,
    },
  ];
  variantData.sort((a, b) => {
    return listingData.findIndex(l => l.type === a.type) - listingData.findIndex(l => l.type === b.type);
  });

  // PURCHASES
  const [now] = useState(() => Date.now());
  const thirtyDaysAgo = new Date(now - 30 * 24 * 60 * 60 * 1000);
  const purchasesInTheLastThirtyDays = purchases.filter(purchase => {
    return new Date(purchase.created_at) > thirtyDaysAgo;
  });
  const purchasesByType = purchasesInTheLastThirtyDays.reduce(
    (acc, purchase) => {
      acc[purchase.variant_type] = (acc[purchase.variant_type] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>,
  );
  const purchaseData = Object.entries(purchasesByType).map(([type, count]) => ({ type, count }));
  purchaseData.sort((a, b) => {
    return listingData.findIndex(l => l.type === a.type) - listingData.findIndex(l => l.type === b.type);
  });

  return (
    <Card title="Market Share by Variant Type">
      <Box css={{ px: 16, pt: 16, fontScale: "sm", color: colors.text2, weight: 500 }}>
        <Text>Published Variants</Text>
        <OneDimensionalStackedBarChart data={variantData} />

        <Box css={{ height: 8 }} />

        <Text>Published Listings (Listings with multiple variant types count for each type)</Text>
        <OneDimensionalStackedBarChart data={listingData} />

        <Box css={{ height: 8 }} />

        <Text>Purchases in last 30 days</Text>
        <OneDimensionalStackedBarChart data={purchaseData} />
      </Box>
    </Card>
  );
}

function OneDimensionalStackedBarChart({ data }: { data: { type: string; count: number }[] }) {
  const backgroundByType = {
    Booking: `url(#${bookingPattern})`,
    Product: `url(#${productPattern})`,
    Event: `url(#${eventPattern})`,
    Subscription: `url(#${subscriptionPattern})`,
    Pass: `url(#${passPattern})`,
  };
  const total = data.reduce((acc, { count }) => acc + count, 0);
  return (
    <Box css={{ height: 64, width: "100%", display: "flex", borderRadius: 8, overflow: "hidden" }}>
      {data.map(datapoint => {
        return (
          <Box
            key={datapoint.type}
            css={{ flex: "1 0 0px", position: "relative" }}
            style={{ flexGrow: datapoint.count }}
          >
            <Box as="svg" css={{ width: "100%", height: "100%" }}>
              <rect
                width="1000"
                height="1000"
                fill={backgroundByType[datapoint.type as keyof typeof backgroundByType]}
              />
            </Box>
            <Text
              css={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                // backgroundColor: "rgba(0,0,0,0.1)",
                color: "white",
                px: 4,
                // backdropFilter: "blur(3px)",
                mixBlendMode: "luminosity",
                backgroundColor: "rgba(128,128,128,0.4)",
                borderRadius: 4,
              }}
            >
              {Math.round((datapoint.count / total) * 100)}%
            </Text>
          </Box>
        );
      })}
    </Box>
  );
}

// Histogram bin edges from zero to just past `max`, with the width snapped to a
// 1/2/5-multiple step so the edges land on round numbers.
function niceBinBoundaries(max: number, targetBins: number): number[] {
  if (!(max > 0)) return [0, 1];
  const rawStep = max / targetBins;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const normalized = rawStep / magnitude;
  const step = (normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10) * magnitude;
  const binCount = Math.max(1, Math.ceil(max / step));
  return Array.from({ length: binCount + 1 }, (_, i) => i * step);
}

function AllowancePlot() {
  const { data: allowances } = useSuspenseQuery({
    queryKey: ["reporting", "allowances"],
    queryFn: async () => {
      return await api.reporting.getManagementReportingObsRecurringAllowances();
    },
  });
  const { ref, height } = useContainerSize();

  const amounts = useMemo(
    () => allowances.map(d => ({ nzd: convertToNZD(d.token_amount_per_month, d.token_currency_code) })),
    [allowances],
  );

  const definition = useMemo(() => {
    const maxAmount = amounts.reduce((max, d) => Math.max(max, d.nzd), 0);
    const boundaries = niceBinBoundaries(maxAmount, 24);
    const bins = binX(amounts, { value: "nzd", thresholds: boundaries });
    const formatBound = (value: number) => formatDollars(value, "NZD", { maximumFractionDigits: 0 });
    return defineChart({
      marks: [
        rect(bins, { x1: "x1", x2: "x2", y1: () => 0, y2: "value", fill: "currentColor" }),
        ruleY([0], { strokeOpacity: 1 }),
      ],
      scales: {
        x: {
          scale: scaleLinear().domain([0, boundaries[boundaries.length - 1] ?? 1]),
          axis: { label: "Allowance per Month ($NZD)", ticks: { format: formatAxisNumber } },
        },
        y: {
          scale: scaleLinear,
          grid: true,
          axis: { label: "No. of allowances", ticks: { size: 0, format: formatAxisNumber } },
        },
      },
      tooltip: {
        use: tooltip,
        format: point =>
          `${formatBound(point.datum.x1)}–${formatBound(point.datum.x2)}: ${formatWholeNumber(point.datum.value)} allowances`,
      },
      focus: "group-x",
    });
  }, [amounts]);

  return (
    <Card title="Monthly Allowances" css={{ flex: "1 0 0px", display: "flex", flexDirection: "column" }}>
      <ChartBox ref={ref} css={{ height: "100%", flex: "1 0 0px" }}>
        {height !== undefined && <Chart definition={definition} height={height} ariaLabel="Monthly allowances" />}
      </ChartBox>
    </Card>
  );
}

/**
 * Histogram of days-per-member in 30-day buckets (the old beeswarm's rules
 * every 30 days become the bin edges), shared by the two activity charts.
 */
function DaysHistogramChart({
  data,
  xLabel,
  ariaLabel,
}: {
  data: MemberActivityEntry[];
  xLabel: string;
  ariaLabel: string;
}) {
  const { ref, height } = useContainerSize();

  const definition = useMemo(() => {
    const maxDays = data.reduce((max, entry) => Math.max(max, entry.days_since_last_activity), 0);
    const binCount = Math.max(1, Math.ceil(maxDays / 30));
    const boundaries = Array.from({ length: binCount + 1 }, (_, i) => i * 30);
    const bins = binX(data, { value: "days_since_last_activity", thresholds: boundaries });
    return defineChart({
      marks: [
        rect(bins, { x1: "x1", x2: "x2", y1: () => 0, y2: "value", fill: "currentColor" }),
        ruleY([0], { strokeOpacity: 1 }),
      ],
      scales: {
        x: {
          scale: scaleLinear().domain([0, boundaries[boundaries.length - 1] ?? 30]),
          axis: { label: xLabel, ticks: { format: formatAxisNumber } },
        },
        y: {
          scale: scaleLinear,
          grid: true,
          axis: { label: "No. of members", ticks: { size: 0, format: formatAxisNumber } },
        },
      },
      tooltip: {
        use: tooltip,
        format: point => `${point.datum.x1}–${point.datum.x2} days: ${formatWholeNumber(point.datum.value)} members`,
      },
      focus: "group-x",
    });
  }, [data, xLabel]);

  return (
    <ChartBox ref={ref} css={{ height: "100%", flex: "1 0 0px" }}>
      {height !== undefined && <Chart definition={definition} height={height} ariaLabel={ariaLabel} />}
    </ChartBox>
  );
}

function PurchaseGaps() {
  const { data: allowances } = useSuspenseQuery({
    queryKey: ["reporting", "activity-time"],
    queryFn: async () => {
      return await api.reporting.getActivityTime({ supporterId: undefined });
    },
  });
  const data = useMemo(() => allowances.activity.filter(i => i.has_purchased), [allowances]);

  return (
    <Card title="Gap Between Purchases" css={{ flex: "1 0 0px", display: "flex", flexDirection: "column" }}>
      <DaysHistogramChart
        data={data}
        xLabel="Days since last purchase (marketplace or receipt)"
        ariaLabel="Gap between purchases"
      />
    </Card>
  );
}

function DormantAccounts() {
  const { data: allowances } = useSuspenseQuery({
    queryKey: ["reporting", "activity-time"],
    queryFn: async () => {
      return await api.reporting.getActivityTime({ supporterId: undefined });
    },
  });
  const data = useMemo(() => allowances.activity.filter(i => !i.has_purchased), [allowances]);

  return (
    <Card title="Accounts Without Purchases" css={{ flex: "1 0 0px", display: "flex", flexDirection: "column" }}>
      <DaysHistogramChart data={data} xLabel="Days since joining" ariaLabel="Accounts without purchases" />
    </Card>
  );
}

type CardProps = Omit<BoxProps, "title"> & {
  title?: ReactNode;
};
function Card({ title, children, ...props }: CardProps) {
  return (
    <Box
      {...props}
      css={{
        pt: 16,
        rounded: 12,
        bg: "white",
        boxShadow: `inset 0 0 0 1px ${colors.outline2}`,
        ...props.css,
      }}
    >
      {title && (
        <Text
          css={{
            fontSize: 25,
            weight: 500,
            lineHeight: 1.25,
            align: "center",
            color: colors.navy,
            mb: 4,
          }}
        >
          {title}
        </Text>
      )}
      {children}
    </Box>
  );
}

/**
 * Despite the keys being token currency codes, these are actually the conversion rates of dollars, not tokens
 */
const LOCAL_TO_NZD: Record<string, number> = {
  autoken: 1.2,
  auarrears: 1.2,
  catoken: 1.22,
  eutoken: 1.96,
  gbtoken: 2.29,
  nztoken: 1,
  nzarrears: 1,

  aetoken: 0.46,
  cntoken: 0.25,
  hktoken: 0.22,
  iltoken: 0.57,
  mxtoken: 0.1,
  sgtoken: 1.33,
  ustoken: 1.69,
  usarrears: 1.69,
} satisfies Record<TokenCurrencyCode, number>;

function convertToNZD(tokens: number, tokenCurrencyCode: string) {
  const localDollars = tokensToMoney({ amount: tokens, tokenCurrencyCode: tokenCurrencyCode as TokenCurrencyCode });
  return localDollars.amount * (LOCAL_TO_NZD[tokenCurrencyCode] ?? 1);
}
